import React, { useMemo } from 'react';
import ReactFlow, { Background, Controls, MarkerType } from 'reactflow';
import dagre from 'dagre';
import { Maximize2, TrendingUp, TrendingDown } from 'lucide-react';
import PlaceNode from './nodes/PlaceNode';
import TransitionNode from './nodes/TransitionNode';

const nodeTypes = {
  place: PlaceNode,
  transition: TransitionNode,
};

// ReactFlow pro options moved outside component to prevent re-renders
const reactFlowProOptions = { hideAttribution: true };

const getLayoutedElements = (nodes, edges) => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ rankdir: 'LR', ranksep: 100, nodesep: 60 });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: node.type === 'place' ? 60 : 120, height: node.type === 'place' ? 60 : 50 });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  nodes.forEach((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    node.position = {
      x: nodeWithPosition.x - (node.type === 'place' ? 30 : 60),
      y: nodeWithPosition.y - (node.type === 'place' ? 30 : 25),
    };
  });

  return { nodes, edges };
};

const ClusteredPetriNetView = ({
  petriNet,
  title,
  numCases,
  isOriginal = false,
  onPreview,
  businessAnalysis,
  selectedParams = null, // Array of selected param keys (e.g., ['total_pegawai', 'total_biaya'])
}) => {
  const { nodes, edges } = useMemo(() => {
    const flowNodes = [
      ...petriNet.places.map((place) => ({
        id: place.id,
        type: 'place',
        data: {
          label: place.label,
          isStart: place.isStart,
          isEnd: place.isEnd,
          tokens: place.tokens,
        },
        position: { x: 0, y: 0 },
      })),
      ...petriNet.transitions.map((transition) => ({
        id: transition.id,
        type: 'transition',
        data: {
          label: transition.label,
          isSilent: transition.isSilent,
        },
        position: { x: 0, y: 0 },
      })),
    ];

    const flowEdges = petriNet.arcs.map((arc) => ({
      id: arc.id,
      source: arc.source,
      target: arc.target,
      type: 'smoothstep',
      animated: false,
      style: { stroke: '#71717A', strokeWidth: 1.5 },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: '#71717A',
      },
    }));

    return getLayoutedElements(flowNodes, flowEdges);
  }, [petriNet]);

  // Format helpers
  const formatNumber = (num) => {
    if (num === undefined || num === null) return '-';
    return num.toLocaleString('id-ID');
  };

  const formatCurrency = (num) => {
    if (num === undefined || num === null) return '-';
    return `Rp ${num.toLocaleString('id-ID')}`;
  };

  const formatDuration = (num) => {
    if (num === undefined || num === null || num === 0) return '-';
    if (num >= 1440) {
      const days = Math.floor(num / 1440);
      const hours = Math.floor((num % 1440) / 60);
      return `${days.toLocaleString('id-ID')} hari ${hours} jam`;
    } else if (num >= 60) {
      const hours = Math.floor(num / 60);
      const mins = Math.round(num % 60);
      return `${hours.toLocaleString('id-ID')} jam ${mins} menit`;
    }
    return `${Math.round(num).toLocaleString('id-ID')} menit`;
  };

  // Determine which business metrics to show
  // - For Original: always show all 6 extreme metrics
  // - For clusters: only show metrics matching selected param keys
  const PARAM_META = {
    pegawai_terbanyak: { label: 'Pegawai Terbanyak', icon: TrendingUp, color: 'blue', format: 'number' },
    pegawai_tersedikit: { label: 'Pegawai Tersedikit', icon: TrendingDown, color: 'cyan', format: 'number' },
    biaya_termahal: { label: 'Biaya Termahal', icon: TrendingUp, color: 'red', format: 'currency' },
    biaya_termurah: { label: 'Biaya Termurah', icon: TrendingDown, color: 'green', format: 'currency' },
    waktu_terlama: { label: 'Waktu Terlama', icon: TrendingUp, color: 'orange', format: 'duration' },
    waktu_terpendek: { label: 'Waktu Terpendek', icon: TrendingDown, color: 'emerald', format: 'duration' },
  };

  const formatValue = (value, fmt) => {
    if (fmt === 'currency') return formatCurrency(value);
    if (fmt === 'duration') return formatDuration(value);
    return formatNumber(value);
  };

  const colorTextClass = {
    blue: 'text-blue-600', cyan: 'text-cyan-600', red: 'text-red-600',
    green: 'text-green-600', orange: 'text-orange-600', emerald: 'text-emerald-600',
  };
  const colorValueClass = {
    blue: 'text-blue-800', cyan: 'text-cyan-800', red: 'text-red-800',
    green: 'text-green-800', orange: 'text-orange-800', emerald: 'text-emerald-800',
  };

  const allParamKeys = Object.keys(PARAM_META);
  const clusterParamKeys = selectedParams && selectedParams.length > 0 ? selectedParams : allParamKeys;
  const visibleMetricsCount = (isOriginal ? allParamKeys : clusterParamKeys).length;

  return (
    <div
      className="bg-white rounded-2xl border-2 border-gray-300 overflow-hidden relative group shadow-md hover:shadow-xl transition-shadow"
      data-testid={isOriginal ? 'original-petrinet-card' : 'cluster-petrinet-card'}
    >
      <div className={`px-4 py-3 border-b border-gray-300 ${isOriginal ? 'bg-gradient-to-r from-gray-100 to-gray-50' : 'bg-gradient-to-r from-emerald-50 to-lime-50'}`}>
        {/* Title + Preview Button + Petri Net Stats inline */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-baseline gap-3 flex-wrap">
            <h3 className="text-base font-bold text-zinc-800" style={{ fontFamily: 'Syne, sans-serif' }}>
              {title}
            </h3>
            <div className="flex items-center gap-3 text-xs text-zinc-600">
              <span><span className="font-semibold text-indigo-700">{formatNumber(numCases || 0)}</span> Cases</span>
              <span className="text-gray-300">·</span>
              <span><span className="font-semibold text-purple-700">{formatNumber(petriNet.places.length)}</span> Places</span>
              <span className="text-gray-300">·</span>
              <span><span className="font-semibold text-pink-700">{formatNumber(petriNet.transitions.length)}</span> Transitions</span>
            </div>
          </div>
          {!isOriginal && onPreview && (
            <button
              onClick={onPreview}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-100 hover:bg-emerald-200 border border-emerald-300 text-emerald-700 rounded-md transition-all text-xs font-medium"
              data-testid="cluster-preview-button"
            >
              <Maximize2 className="w-3.5 h-3.5" />
              Preview
            </button>
          )}
        </div>

        {/* Business Analysis Info - compact inline chips */}
        {businessAnalysis && visibleMetricsCount > 0 && (
          <div className="mt-2 pt-2 border-t border-gray-200">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
              {(isOriginal ? allParamKeys : clusterParamKeys).map((key) => {
                const meta = PARAM_META[key];
                if (!meta) return null;
                const value = businessAnalysis[key];
                if (value === undefined || value === null) return null;
                const Icon = meta.icon;
                return (
                  <span key={key} className="inline-flex items-center gap-1" title={`Total ${meta.label}`}>
                    <Icon className={`w-3 h-3 ${colorTextClass[meta.color]}`} />
                    <span className="text-zinc-500">{meta.label}:</span>
                    <span className={`font-semibold ${colorValueClass[meta.color]}`}>
                      {formatValue(value, meta.format)}
                    </span>
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </div>
      <div className={`${isOriginal ? 'h-[520px]' : 'h-[440px]'} bg-gray-50 relative`}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          fitView
          proOptions={reactFlowProOptions}
          zoomOnScroll={true}
          panOnDrag={true}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={true}
          minZoom={0.1}
          maxZoom={4}
        >
          <Background color="#d1d5db" gap={32} style={{ backgroundColor: '#f9fafb' }} />
          <Controls className="bg-white border-2 border-gray-300 rounded-lg shadow-lg" showInteractive={false} />
        </ReactFlow>
      </div>
    </div>
  );
};

export default ClusteredPetriNetView;
